<template>
  <main v-if="bundle" class="main-container">
    <h1 class="mb-4">Release bundle</h1>
    <div class="d-flex flex-row justify-space-between">
      <div>
        <h3>
          Name:
          <span class="bundle-info-value">{{ bundle.name }}</span>
        </h3>
        <h3>
          Version:
          <span class="bundle-info-value">{{
            removePatchVersion(bundle.version)
          }}</span>
        </h3>
        <h3>
          Build:
          <span class="bundle-info-value">{{ bundle.lastBuildNumber }}</span>
        </h3>
        <h3>
          Channels:
          <span class="bundle-info-value">{{
            bundle.releaseChannels
              ? bundle.releaseChannels.map((c) => channelToText(c)).join(', ')
              : ''
          }}</span>
        </h3>
      </div>
      <v-btn v-if="!readonly" class="ml-6" @click="openEditReleaseBundleInfo"
        >Edit</v-btn
      >
    </div>

    <v-divider class="my-4" />

    <!-- RELEASE NOTES -->
    <v-expansion-panels accordion class="mt-4 mb-2" flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <h2>Release Notes</h2>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-btn class="mb-2" @click="copyReleaseNotesToClipboard">
            <span class="mr-1">Copy</span>
            <v-icon v-if="copiedToClipboard" color="green"> mdi-check </v-icon>
            <v-icon v-else>mdi-clipboard-text</v-icon>
          </v-btn>
          <ReleaseNotesEditor
            v-if="releaseNotes"
            class="release-note-editor"
            :releaseNotes="releaseNotes"
            :readonly="readonly"
            @save="updateReleaseNotes"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-divider class="my-4" />

    <div>
      <div class="upgrade-package-header">
        <div class="text-h5">Upgrade packages</div>
        <div v-if="!readonly" class="upgrade-package-actions">
          <v-btn color="primary" @click="openAddNewUpgradePackageDialog">
            Add package
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            :loading="isUpdateAllLoading"
            color="primary"
            @click="updateAllPackagesToLatestPatch"
          >
            Update all
            <v-icon>mdi-update</v-icon>
          </v-btn>
        </div>
      </div>
      <v-data-table
        :headers="packageHeaders"
        :items="bundle.packages"
        hide-default-footer
        disable-pagination
        :item-class="rowClass"
        :custom-sort="customSort"
        :sort-by="['version']"
        :sort-desc="[false]"
        must-sort
      >
        <!-- PREVIOUS RELEASE -->
        <template v-slot:item.previousRelease="{ item }">
          <span>
            {{ getPackageInfo(item).prevReleaseVersion || '—' }}
          </span>
        </template>

        <!-- VERSION -->
        <template v-slot:item.version="{ item }">
          <div class="d-flex flex-row align-center">
            <div>{{ item.version }}</div>
            <v-tooltip v-if="versionDiff(item)" top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" class="ml-2">
                  mdi-check-circle-outline
                </v-icon>
              </template>
              <span>Version changed since previous release</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.newAvailable="{ item }">
          <v-tooltip top v-if="getPackageInfo(item).newPatchVersion">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                class="mx-1"
                color="warning"
                text-color="white"
              >
                {{ getPackageInfo(item).newPatchVersion }}
              </v-chip>
            </template>
            <span>New patch available</span>
          </v-tooltip>
          <v-tooltip top v-if="getPackageInfo(item).newLatestVersion">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                class="mx-1"
                color="primary"
                text-color="white"
              >
                {{ getPackageInfo(item).newLatestVersion }}
              </v-chip>
            </template>
            <span>Latest upgrade package</span>
          </v-tooltip>
        </template>

        <!-- PACKAGE ACTIONS -->
        <template v-if="!readonly" v-slot:item.actions="{ item }">
          <div class="d-flex flex-row justify-space-betweem align-center">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  :disabled="!item.active"
                  :loading="
                    updatingSinglePackageTemplateId ===
                    item.upgradePackageTemplateId
                  "
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    updateSinglePackageToLatestPatch(
                      item.upgradePackageTemplateId,
                      item.name
                    )
                  "
                >
                  <v-icon>mdi-update</v-icon>
                </v-btn>
              </template>
              <span>Update to latest patch</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  :disabled="!item.active"
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    openSetUpgradePackageForReleaseDialog(
                      item.upgradePackageTemplateId,
                      item.name
                    )
                  "
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit upgrade package</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.active="{ item }">
          <v-checkbox
            :disabled="readonly"
            dense
            v-model="item.active"
            @change="
              updatePackageActiveState(item.upgradePackageTemplateId, $event)
            "
          />
        </template>
      </v-data-table>
    </div>

    <!-- EDIT RELEASE BUNDLE INFO DIALOG -->
    <v-dialog
      v-if="!readonly"
      v-model="editReleaseBundleInfoData.show"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title>Edit release bundle info</v-card-title>
        <v-card-text>
          <v-form
            ref="editReleaseBundleInfoForm"
            v-model="editReleaseBundleInfoData.valid"
          >
            <v-text-field
              v-model="editReleaseBundleInfoData.name"
              label="Name"
            />
            <v-combobox
              v-model="editReleaseBundleInfoData.releaseChannels"
              :items="CHANNELS"
              :rules="[rules.required]"
              label="Release Channels"
              item-text="name"
              item-value="value"
              multiple
              chips
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="secondary"
            @click="editReleaseBundleInfoData.show = false"
          >
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="editReleaseBundleInfoData.isLoading"
            @click="updateReleaseBundleInfo"
            >Update</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADD NEW UPGRADE PACKAGE DIALOG -->
    <v-dialog
      v-if="!readonly"
      v-model="addNewUpgradePackageData.show"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title>
          <span>Add a new upgrade package to the bundle</span>
        </v-card-title>
        <v-card-text>
          <v-form
            ref="addNewUpgradePackageForm"
            v-model="addNewUpgradePackageData.valid"
          >
            <v-select
              v-model="addNewUpgradePackageData.upgradePackageTemplateId"
              :items="upgradePackageTemplates"
              item-value="id"
              item-text="name"
              label="Upgrade package template"
              :rules="[rules.required]"
            />
            <v-select
              v-model="addNewUpgradePackageData.upgradePackageId"
              :items="upgradePackagesForSelectedTemplate"
              label="Version"
              item-value="id"
              item-text="version"
              :rules="[rules.required]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="addNewUpgradePackageData.show = false">
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="addNewUpgradePackage"
            :loading="addNewUpgradePackageData.isLoading"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SET UPGRADE PACKAGE DIALOG -->
    <v-dialog
      v-if="!readonly"
      v-model="setUpgradePackageForReleaseData.show"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title>
          <span>{{
            `Set upgrade package for ${setUpgradePackageForReleaseData.name}`
          }}</span>
        </v-card-title>
        <v-card-text>
          <v-form
            ref="setUpgradePackageForReleaseForm"
            v-model="setUpgradePackageForReleaseData.valid"
          >
            <v-select
              v-model="setUpgradePackageForReleaseData.upgradePackageId"
              :items="upgradePackagesForSelectedTemplate"
              label="Version"
              item-value="id"
              item-text="version"
              :rules="[rules.required]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="setUpgradePackageForReleaseData.show = false">
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="setUpgradePackageForRelease"
            :loading="setUpgradePackageForReleaseData.isLoading"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ERROR POPUP -->
    <Popup
      :show="showErrorMessage"
      :text="errorMessage"
      title="Error"
      type="error"
      @close="showErrorMessage = false"
    />
    <!-- CONFIRMATION DIALOG -->
    <confirmation-dialog ref="confirmationDialog" />
  </main>
  <div v-else class="loading-container">
    <v-progress-circular indeterminate :size="100" color="primary" />
  </div>
</template>

<script>
import {
  CHANNELS,
  errorToText,
  channelToText,
  removePatchVersion,
} from '../../utils/releaseUtils';
import { omitDeep } from '../../utils/ObjectUtils';
import {
  GET_RELEASE_BUNDLE,
  GET_RELEASE_BUNDLE_BUILD,
  GET_ALL_UPGRADE_PACKAGE_TEMPLATES,
  GET_UPGRADE_PACKAGES_FOR_TEMPLATE,
  ADD_NEW_PACKAGE_TO_RELEASE_BUNDLE,
  SET_UPGRADE_PACKAGE_FOR_RELEASE,
  UPDATE_RELEASE_BUNDLE_INFO,
  UPDATE_SINGLE_PACKAGE_TO_LATEST_PATCH,
  UPDATE_ALL_PACKAGES_TO_LATEST_PATCH,
  UPDATE_PACKAGE_ACTIVE_STATE,
  UPDATE_RELEASE_NOTES_TO_RELEASE_BUNDLE,
} from '../../graphql/upgradePackageQueries';

export default {
  name: 'ReleaseBundleEditor',
  components: {
    Popup: () => import('../common/Popup'),
    ConfirmationDialog: () => import('../common/CConfirmationDialog'),
    ReleaseNotesEditor: () => import('./ReleaseNotesEditor'),
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    // Option to pass the id of a generated release bundle release instead of getting working copy from route
    releaseBundleBuildId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      CHANNELS,
      diffsFirst: true,
      bundle: null,
      copiedToClipboard: false,
      isUpdateAllLoading: false,
      updatingSinglePackageTemplateId: null, // Id for which package is currently updating to latest patch (to show loading)
      errorMessage: '',
      showErrorMessage: false,
      releaseNotes: [],
      upgradePackageTemplates: [], // All templates
      upgradePackagesForSelectedTemplate: [], // (when adding new package or setting new upgrade package for specific package)
      editReleaseBundleInfoData: {
        show: false,
        valid: false,
        isLoading: false,
        name: '',
        releaseChannels: [],
      },
      addNewUpgradePackageData: {
        show: false,
        valid: false,
        isLoading: false,
        upgradePackageTemplateId: null,
        upgradePackageId: null,
      },
      setUpgradePackageForReleaseData: {
        show: false,
        valid: false,
        isLoading: false,
        name: '',
        upgradePackageTemplateId: null,
        upgradePackageId: null,
      },
      rules: {
        required: (v) => (!!v && v.length > 0) || 'Required',
      },
    };
  },
  async mounted() {
    await this.fetchReleaseBundle();
  },
  apollo: {
    $client: 'upgradePackageClient',
    upgradePackageTemplates: {
      query: GET_ALL_UPGRADE_PACKAGE_TEMPLATES,
      fetchPolicy: 'no-cache',
      update: (data) => data.getAllUpgradePackageTemplates,
    },
  },
  watch: {
    releaseBundleBuildId(newId) {
      if (newId) this.fetchReleaseBundleBuild();
    },
    addNewUpgradePackageData: {
      async handler(newVal) {
        if (newVal.upgradePackageTemplateId) {
          // Get the upgrade packages for the selected template
          this.upgradePackagesForSelectedTemplate =
            await this.getUpgradePackagesForTemplate(
              newVal.upgradePackageTemplateId
            );
        }
      },
      deep: true,
    },
    setUpgradePackageForReleaseData: {
      async handler(newVal) {
        if (newVal.show && newVal.upgradePackageTemplateId) {
          // Get the upgrade packages for the selected template
          this.upgradePackagesForSelectedTemplate =
            await this.getUpgradePackagesForTemplate(
              newVal.upgradePackageTemplateId
            );
        }
      },
      deep: true,
    },
  },
  computed: {
    packageHeaders() {
      const headers = [
        { text: 'Name', value: 'name' },
        { text: 'Previous release', value: 'previousRelease' },
        { text: 'Version', value: 'version' },
        { text: 'New available', value: 'newAvailable' },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
        },
        {
          text: 'Active',
          value: 'active',
        },
      ];

      // Remove actions column if readonly
      if (this.readonly) headers.splice(headers.length - 2, 1);

      return headers;
    },
  },
  methods: {
    removePatchVersion,
    channelToText,
    openEditReleaseBundleInfo() {
      this.editReleaseBundleInfoData.name = this.bundle.name;
      this.editReleaseBundleInfoData.releaseChannels =
        this.bundle.releaseChannels.map((c) => {
          const channel = CHANNELS.find((ch) => ch.value === c);
          if (channel) return channel;
          return { name: c, value: c };
        });

      this.editReleaseBundleInfoData.isLoading = false;
      this.editReleaseBundleInfoData.valid = true;
      this.editReleaseBundleInfoData.show = true;
    },
    openAddNewUpgradePackageDialog() {
      this.addNewUpgradePackageData.upgradePackagesForTemplate = null;
      this.addNewUpgradePackageData.upgradePackageTemplateId = null;
      this.addNewUpgradePackageData.upgradePackageId = null;
      this.addNewUpgradePackageData.valid = true;
      this.addNewUpgradePackageData.isLoading = false;
      this.addNewUpgradePackageData.show = true;
    },
    openSetUpgradePackageForReleaseDialog(upgradePackageTemplateId, name) {
      this.setUpgradePackageForReleaseData.upgradePackageTemplateId =
        upgradePackageTemplateId;
      this.setUpgradePackageForReleaseData.name = name;
      this.setUpgradePackageForReleaseData.upgradePackageId = null;
      this.setUpgradePackageForReleaseData.valid = true;
      this.setUpgradePackageForReleaseData.show = true;
      this.setUpgradePackageForReleaseData.isLoading = false;
    },
    versionDiff(item) {
      const prev = this.getPackageInfo(item).prevReleaseVersion;
      return !!prev && prev !== item.version;
    },
    rowClass(item) {
      return item.active ? '' : 'disabled-row';
    },
    customSort(items, sortBy, sortDesc) {
      const by = Array.isArray(sortBy) ? sortBy[0] : sortBy;
      const desc = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc;
      const withIndex = items.map((it, i) => ({ it, i }));

      withIndex.sort((A, B) => {
        const a = A.it;
        const b = B.it;

        // When sorting the version column, we sort by if the version is changed since last release
        if (by === 'version') {
          const aSort = this.versionDiff(a) ? 0 : 1;
          const bSort = this.versionDiff(b) ? 0 : 1;
          if (aSort !== bSort) return desc ? bSort - aSort : aSort - bSort;

          return A.i - B.i; // Sort by index if they are in the same group
        }

        // Simple fallback sort by the requested column
        const av = a?.[by];
        const bv = b?.[by];
        let cmp = 0;
        if (av == null && bv != null) cmp = -1;
        else if (av != null && bv == null) cmp = 1;
        else
          cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          });

        if (cmp !== 0) return desc ? -cmp : cmp;
        return A.i - B.i;
      });

      return withIndex.map((x) => x.it);
    },
    async copyReleaseNotesToClipboard() {
      try {
        if (!this.releaseNotes) return;

        let text = '';

        if (this.releaseNotes.info) {
          text += '### Info ###\n';
          text += this.releaseNotes.info;
          text += '\n\n';
        }

        if (this.releaseNotes.features?.length) {
          text += '### Features ###\n';
          text += this.releaseNotes.features
            .map((f) => `- ${f.title}: ${f.description}`)
            .join('\n');
          text += '\n\n';
        }

        if (this.releaseNotes.fixes?.length) {
          text += '### Fixes ###\n';
          text += this.releaseNotes.fixes
            .map((f) => `- ${f.title}: ${f.description}`)
            .join('\n');
        }

        await navigator.clipboard.writeText(text);

        // Display a checkmark for 2 seconds after copying
        this.copiedToClipboard = true;
        setTimeout(() => {
          this.copiedToClipboard = false;
        }, 2000);
      } catch (err) {
        console.error(err);
        this.displayError('Failed to copy release notes to clipboard');
      }
    },
    async fetchReleaseBundle() {
      try {
        // Get release bundle from already generated release bundle release instead of from working copy
        if (this.releaseBundleBuildId) {
          const response = await this.$apollo.query({
            query: GET_RELEASE_BUNDLE_BUILD,
            fetchPolicy: 'no-cache',
            variables: {
              releaseBundleBuildId: this.releaseBundleBuildId,
              includeInfo: true,
            },
          });
          const bundleRelease = response.data.getReleaseBundleBuild;
          if (!bundleRelease) {
            this.displayError('Failed to get release bundle');
            return;
          }

          this.bundle = bundleRelease.releaseBundle;
          this.releaseNotes = omitDeep(this.bundle.releaseNotes, '__typename');
        } else {
          // Use id from provided bundle if any
          const id = this.$route.params.id;
          if (!id) {
            this.displayError('No release bundle ID provided');
            return;
          }

          const res = await this.$apollo.query({
            query: GET_RELEASE_BUNDLE,
            fetchPolicy: 'no-cache',
            variables: {
              id: id,
              includeInfo: true,
            },
          });
          const bundle = res.data.getReleaseBundle;

          if (!bundle) {
            this.displayError('Failed to get release bundle');
            return;
          }

          this.bundle = bundle;
          this.releaseNotes = omitDeep(bundle.releaseNotes, '__typename');
        }
      } catch (err) {
        console.error(err);
        this.displayError('Failed to get release bundle');
      }
    },
    async getUpgradePackagesForTemplate(templateId) {
      try {
        const res = await this.$apollo.query({
          query: GET_UPGRADE_PACKAGES_FOR_TEMPLATE,
          fetchPolicy: 'no-cache',
          variables: {
            upgradePackageTemplateId: templateId,
          },
        });
        const upgradePackages = res.data.getUpgradePackagesForTemplate;

        if (!upgradePackages) {
          this.displayError('Failed to get upgrade packages for template');
          return;
        }

        return upgradePackages;
      } catch (err) {
        console.error(err);
        this.displayError('Failed to get upgrade packages for template');
      }
    },
    async updateReleaseBundleInfo() {
      try {
        if (!this.$refs.editReleaseBundleInfoForm.validate()) return;

        this.editReleaseBundleInfoData.isLoading = true;

        const response = await this.$apollo.mutate({
          mutation: UPDATE_RELEASE_BUNDLE_INFO,
          variables: {
            id: this.bundle.id,
            version: this.bundle.version, // Use the same version as before
            name: this.editReleaseBundleInfoData.name,
            releaseChannels: this.editReleaseBundleInfoData.releaseChannels.map(
              (c) => c.value
            ),
          },
        });
        const res = response.data.updateReleaseBundleInfo;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        this.editReleaseBundleInfoData.show = false;
        this.bundle.name = res.releaseBundle.name;
        this.bundle.releaseChannels = res.releaseBundle.releaseChannels;
      } catch (err) {
        console.error(err);
        this.displayError('Failed to update release bundle info');
      } finally {
        this.editReleaseBundleInfoData.isLoading = false;
      }
    },
    async addNewUpgradePackage() {
      try {
        this.addNewUpgradePackageData.isLoading = true;
        if (!this.$refs.addNewUpgradePackageForm.validate()) return;

        const response = await this.$apollo.mutate({
          mutation: ADD_NEW_PACKAGE_TO_RELEASE_BUNDLE,
          variables: {
            releaseBundleId: this.bundle.id,
            upgradePackageTemplateId:
              this.addNewUpgradePackageData.upgradePackageTemplateId,
            upgradePackageId: this.addNewUpgradePackageData.upgradePackageId,
          },
        });

        const res = response.data.addNewPackageToReleaseBundle;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        // Refetches bundle to recalculate info for available versions, since we added a new package
        await this.fetchReleaseBundle();
        this.addNewUpgradePackageData.show = false;
      } catch (err) {
        console.error(err);
        this.displayError('Failed to add new upgrade package');
      } finally {
        this.addNewUpgradePackageData.isLoading = false;
      }
    },
    async setUpgradePackageForRelease() {
      try {
        this.setUpgradePackageForReleaseData.isLoading = true;
        if (!this.$refs.setUpgradePackageForReleaseForm.validate()) return;

        const response = await this.$apollo.mutate({
          mutation: SET_UPGRADE_PACKAGE_FOR_RELEASE,
          variables: {
            releaseBundleId: this.bundle.id,
            upgradePackageTemplateId:
              this.setUpgradePackageForReleaseData.upgradePackageTemplateId,
            upgradePackageId:
              this.setUpgradePackageForReleaseData.upgradePackageId,
          },
        });
        const res = response.data.setUpgradePackageForRelease;
        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }
        // Refetches bundle to recalculate info for available versions, since we modified a package
        await this.fetchReleaseBundle();
        this.setUpgradePackageForReleaseData.show = false;
      } catch (err) {
        console.error(err);
        this.displayError('Failed to set upgrade package for release');
      } finally {
        this.setUpgradePackageForReleaseData.isLoading = false;
      }
    },
    async updateAllPackagesToLatestPatch() {
      try {
        if (
          !(await this.$refs.confirmationDialog.open(
            `Are you sure you want to update all packages to the latest patch version available?`
          ))
        )
          return;

        this.isUpdateAllLoading = true;

        const response = await this.$apollo.mutate({
          mutation: UPDATE_ALL_PACKAGES_TO_LATEST_PATCH,
          variables: {
            releaseBundleId: this.bundle.id,
          },
        });

        const res = response.data.updateAllPackagesToLatestPatch;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        // Refetches bundle to recalculate info for available versions, since we modified packages
        await this.fetchReleaseBundle();
      } catch (err) {
        console.error(err);
        this.displayError('Failed to update all packages to latest patch');
      } finally {
        this.isUpdateAllLoading = false;
      }
    },
    async updateSinglePackageToLatestPatch(
      upgradePackageTemplateId,
      packageName
    ) {
      try {
        if (
          !(await this.$refs.confirmationDialog.open(
            `Are you sure you want to update package ${packageName} to the latest patch version`
          ))
        )
          return;

        this.updatingSinglePackageTemplateId = upgradePackageTemplateId;

        const response = await this.$apollo.mutate({
          mutation: UPDATE_SINGLE_PACKAGE_TO_LATEST_PATCH,
          variables: {
            releaseBundleId: this.bundle.id,
            upgradePackageTemplateId: upgradePackageTemplateId,
          },
        });

        const res = response.data.updateSinglePackageToLatestPatch;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        // Refetches bundle to recalculate info for available versions, since we modified packages
        await this.fetchReleaseBundle();
      } catch (err) {
        console.error(err);
        this.displayError(`Failed to update package ${packageName} to latest`);
      } finally {
        this.updatingSinglePackageTemplateId = null;
      }
    },
    async updatePackageActiveState(upgradePackageTemplateId, active) {
      try {
        const response = await this.$apollo.mutate({
          mutation: UPDATE_PACKAGE_ACTIVE_STATE,
          variables: {
            releaseBundleId: this.bundle.id,
            upgradePackageTemplateId: upgradePackageTemplateId,
            active: active,
          },
        });
        const res = response.data.updatePackageActiveState;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        this.bundle.packages.find(
          (p) => p.upgradePackageTemplateId === upgradePackageTemplateId
        ).active = active;
      } catch (err) {
        console.error(err);
        this.displayError("Failed to update package's active state.");
      }
    },
    async updateReleaseNotes() {
      try {
        const response = await this.$apollo.mutate({
          mutation: UPDATE_RELEASE_NOTES_TO_RELEASE_BUNDLE,
          variables: {
            releaseBundleId: this.bundle.id,
            releaseNotes: this.releaseNotes,
          },
        });
        const res = response.data.updateReleaseNotes;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }
      } catch (err) {
        console.error(err);
        this.displayError('Failed to update release notes');
      }
    },
    getPackageInfo(pkg) {
      const info = this.bundle.info;
      if (!info || !info.packageInfo || !info.packageInfo.length)
        return undefined;

      return info.packageInfo.find(
        (p) => p.upgradePackageTemplateId === pkg.upgradePackageTemplateId
      );
    },
    displayError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
    },
  },
};
</script>

<style scoped>
.main-container {
  padding: 24px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.bundle-info-value {
  color: #007bff;
  font-weight: 400;
}

.upgrade-package-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
}

.upgrade-package-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
}

.release-note-editor {
  width: 100%;
}

::v-deep .v-data-table .disabled-row {
  opacity: 0.5;
  background-color: rgb(218, 218, 218);
}
</style>
